import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismInput } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import GeneralFormsFormFieldsDatepickerFormField from 'lumen/components/general/forms/form-fields/datepicker-form-field';
import type { NewIssue } from 'lumen/controllers/market/parity/parity-manager/addissue';

interface ParityParityManagerBookingDetailsFormSignature {
  Args: {
    back: () => void;
    issue: NewIssue;
    next: () => void;
    setIssueProperty: (property: string, newValue: unknown) => void;
    setIssuePropertyFromEvent: (property: string, event: Event) => void;
  };
}

export default class ParityParityManagerBookingDetailsForm extends Component<ParityParityManagerBookingDetailsFormSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  get bookingId(): string | undefined {
    return this.args.issue?.booking_id;
  }

  get firstName(): string | undefined {
    return this.args.issue?.guest_first_name;
  }

  get isFormValid(): boolean {
    return Boolean(this.firstName && this.lastName && this.bookingId);
  }

  get lastName(): string | undefined {
    return this.args.issue?.guest_last_name;
  }

  get minDate(): Dayjs {
    return this.hotelAndMarketService.getLocalTime();
  }

  @action
  onSelectionCancelDate(value: Dayjs | null): void {
    if (value) {
      this.args.setIssueProperty('cancel_before', value.format('YYYY-MM-DD'));
    } else {
      this.args.setIssueProperty('cancel_before', value);
    }
  }

  <template>
    <div class="pt-400 pr-500 pb-800 pl-500" id="add-issue-form">
      <h2 class="header-title">{{t
          "parity_manager.add_issue.booking_details"
        }}</h2>
      <div class="mt-400 flex flex-row">
        <div class="flex w-1/2 flex-col pr-400">
          <PrismInput
            @label={{t "general.first_name"}}
            @onInput={{fn @setIssuePropertyFromEvent "guest_first_name"}}
            @required={{true}}
            @requiredErrorMessage={{t "general.required"}}
            @value={{@issue.guest_first_name}}
          />
        </div>
        <div class="flex w-1/2 flex-col">
          <PrismInput
            @label={{t "general.last_name"}}
            @onInput={{fn @setIssuePropertyFromEvent "guest_last_name"}}
            @required={{true}}
            @requiredErrorMessage={{t "general.required"}}
            @value={{@issue.guest_last_name}}
          />
        </div>
      </div>
      <div class="flex w-full flex-col">
        <PrismInput
          @label={{t "parity_manager.booking_id"}}
          @onInput={{fn @setIssuePropertyFromEvent "booking_id"}}
          @required={{true}}
          @requiredErrorMessage={{t "general.required"}}
          @value={{@issue.booking_id}}
        />
      </div>
      <h2 class="header-title">{{t
          "parity_manager.test_booking.cancellation"
        }}</h2>
      <div class="mt-200 flex flex-row">
        <div class="flex w-1/2 flex-col">
          <GeneralFormsFormFieldsDatepickerFormField
            {{! @glint-expect-error: Object literal may only specify known properties, and 'datePickerMinDate' does not exist in type 'NamedArgs<...>'. }}
            @datePickerMinDate={{this.minDate}}
            @isOptional={{true}}
            @label={{t "parity_manager.cancel_before"}}
            @onSelectDate={{this.onSelectionCancelDate}}
            @selectedDate={{@issue.cancel_before}}
          />
        </div>
      </div>
    </div>
    <div class="settings-modal__footer flex flex-row justify-end">
      <GeneralFormFieldsCancelAndConfirm
        @cancel={{@back}}
        @cancelText={{t "general.back"}}
        @confirm={{@next}}
        @confirmText={{t "general.next_step"}}
        @isConfirmDisabled={{not this.isFormValid}}
      />
    </div>
  </template>
}
